import type { IChatMessage } from "@/types/(my-space)/chat.interface";

/** API bounds for `q` (search-messages DTO): shorter is a 400, longer is cut. */
export const MESSAGE_SEARCH_MIN_LENGTH = 2;
export const MESSAGE_SEARCH_MAX_LENGTH = 100;

export const normalizeSearchQuery = (raw: string) =>
  raw.trim().slice(0, MESSAGE_SEARCH_MAX_LENGTH);

export const isSearchableQuery = (raw: string) =>
  normalizeSearchQuery(raw).length >= MESSAGE_SEARCH_MIN_LENGTH;

/** A run of text, marked when it is an occurrence of the query. */
export interface HighlightSegment {
  text: string;
  match: boolean;
}

const ELLIPSIS = "…";

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Case-insensitive, Unicode-aware. Matching runs on the original string (no
 * lower-casing copy), so indices never drift on characters whose lower case
 * has a different length.
 */
const queryRegExp = (query: string) => {
  const q = query.trim();
  return q ? new RegExp(escapeRegExp(q), "giu") : null;
};

/** Index of the first occurrence of `query` in `text`, or -1. */
export const firstMatchIndex = (text: string, query: string) => {
  const re = queryRegExp(query);
  if (!re) return -1;
  const m = re.exec(text);
  return m ? m.index : -1;
};

/** `text` split into plain and matching runs — render matches as <mark>, never as HTML. */
export const highlightSegments = (text: string, query: string): HighlightSegment[] => {
  const re = queryRegExp(query);
  if (!re || !text) return text ? [{ text, match: false }] : [];
  const out: HighlightSegment[] = [];
  let last = 0;
  for (const m of text.matchAll(re)) {
    const start = m.index ?? 0;
    if (start > last) out.push({ text: text.slice(last, start), match: false });
    out.push({ text: m[0], match: true });
    last = start + m[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last), match: false });
  return out;
};

/**
 * One line of `text` around the first match, at most about `maxLength`
 * characters, with `…` where it was cut — the match sits roughly a third of
 * the way in so some context before it shows. Cuts snap to a nearby space so
 * a word isn't sliced in half. Whitespace (newlines too) collapses to one space.
 */
export const searchSnippet = (
  rawText: string,
  query: string,
  maxLength = 90,
): HighlightSegment[] => {
  const text = rawText.replace(/\s+/g, " ").trim();
  if (text.length <= maxLength) return highlightSegments(text, query);

  const at = Math.max(firstMatchIndex(text, query), 0);
  let start = Math.max(0, at - Math.floor(maxLength / 3));
  let end = Math.min(text.length, start + maxLength);
  // Near the end: slide the window back so it stays full.
  if (end - start < maxLength) start = Math.max(0, end - maxLength);

  const SNAP = 12;
  if (start > 0) {
    const space = text.indexOf(" ", start);
    if (space !== -1 && space - start <= SNAP && space < at) start = space + 1;
  }
  if (end < text.length) {
    const space = text.lastIndexOf(" ", end);
    const matchEnd = at + query.trim().length;
    if (space !== -1 && end - space <= SNAP && space >= matchEnd) end = space;
  }

  const slice = text.slice(start, end).trim();
  return highlightSegments(
    `${start > 0 ? ELLIPSIS : ""}${slice}${end < text.length ? ELLIPSIS : ""}`,
    query,
  );
};

/**
 * The text a search result shows: the first of the message's searchable
 * fields (as the API matches them — text/caption, file names, link title and
 * url, location name, contact name) that contains the query, else the first
 * that is set. Empty when the message has none (the caller falls back to its
 * content preview).
 */
export const searchResultText = (
  m: Pick<IChatMessage, "body" | "attachments" | "metadata" | "poll">,
  query: string,
): string => {
  const meta = m.metadata ?? {};
  const candidates = [
    m.body,
    ...m.attachments.map((a) => a.fileName),
    meta.link?.title,
    meta.link?.url,
    meta.location?.name,
    meta.location?.address,
    meta.contact?.name,
    m.poll?.question,
  ]
    .map((s) => s?.trim() ?? "")
    .filter(Boolean);
  return candidates.find((s) => firstMatchIndex(s, query) !== -1) ?? candidates[0] ?? "";
};
